import { ListPagination } from "Common/ListPagination";
import TableContainer from "Common/TableContainer";
import TableEntry from "Common/TableEntry";
import moment from "moment";
import { useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { authServices } from "Utils/auth/services";

interface CasinoBetsTableProps {
  casinoBetsData: CasinoBetsData[];
  page: number;
  limit: number;
  listTotal: ProfitLossTotal;
  handlePageFilter: (value: number, limit: number) => void;
  showAction?: boolean,
  handleStatusUpdate?: any,
  actionType?: string | undefined
  marketIdAction?: string | undefined
  handleExpo?: (marketId: string, userId: string) => void
  betType?: string
}

const CasinoBetsTable = ({
  betType,
  casinoBetsData,
  page,
  limit,
  listTotal,
  handlePageFilter,
  handleStatusUpdate,
  showAction,
  actionType,
  marketIdAction,
  handleExpo
}: CasinoBetsTableProps) => {
  const [showList, setList] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [agentList, setAgentList] = useState<CasinoUserAgent[]>([]);

  const toggleList = (name?: string) => {
    setAgentList([]);
    setList(!showList);
    setSelectedUser(name || "");
  };

  const getUserAgent = async (userId: string) => {
    const { response } = await authServices.getShowAgent({
      user_id: userId,
    });
    if (response?.data) {
      setAgentList(response?.data?.agents);
    }
  };

  const defaultTable = useMemo(() => {
    if (!casinoBetsData || !Array.isArray(casinoBetsData)) {
      return [];
    }
    return casinoBetsData.map((items, index: number) => ({
      no: ((page - 1) * limit) + index + 1,
      date: items?.createdAt,
      domainName: items?.domainName || "",
      username: items?.userName,
      market: items?.marketName,
      match: items?.matchName,
      market_Id: items?.marketId,
      selection: items?.runnerName,
      isBack: items?.isBack,
      rate: items?.odds,
      stake: items?.stake,
      pnl: items?.pnl.toFixed(2),
      roundId: items?.roundId,
      userId: items?.userId,
      id: items?._id
    }));
  }, [casinoBetsData]);

  const columns = useMemo(
    () => [
      {
        header: "No.",
        accessorKey: "no",
        enableColumnFilter: false,
      },

      {
        header: "Date",
        accessorKey: "date",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <div>
              <p className="mb-0">
                {moment(cell?.row?.original?.date).format("DD-MM-YY")}
              </p>
              <p className="mb-0">
                {moment(cell?.row?.original?.date).format(" hh:mm:ss:SSS A")}
              </p>
            </div>
          );
        },
      },
      {
        header: "Domain",
        accessorKey: "domainName",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return <span>{cell?.row?.original?.domainName}</span>;
        },
      },
      {
        header: "User Name",
        accessorKey: "username",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <span
              className="cursor-pointer text-info"
              onClick={() => {
                toggleList(cell?.row?.original?.username);
                getUserAgent(cell?.row?.original?.userId);
              }}
            >
              {cell?.row?.original?.username}
            </span>
          );
        },
      },
      {
        header: "Market | Match",
        accessorKey: "market",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <div>
              <p className="mb-0">
                <strong>{cell?.row?.original?.market}</strong>
              </p>
              <p className="mb-0 text-muted">{cell?.row?.original?.match}</p>
            </div>
          );
        },
      },
      {
        header: "Selection | Type",
        accessorKey: "selection",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <div>
              <p className="mb-0">
                <strong>{cell?.row?.original?.selection}</strong>
              </p>
              <p
                className={`mb-0 btn-${cell?.row?.original?.isBack ? "back" : "lay"
                  } d-inline px-2 rounded-pill`}
              >
                {cell?.row?.original?.isBack ? "Back" : "Lay"}
              </p>
            </div>
          );
        },
      },
      {
        header: "Rate",
        accessorKey: "rate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return <span>{cell?.row?.original?.rate}</span>;
        },
      },
      {
        header: "Stake",
        accessorKey: "stake",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return <span>{cell?.row?.original?.stake?.toFixed(2)}</span>;
        },
      },
      {
        header: "P | L",
        accessorKey: "pnl",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <span
              className={`cursor-pointer text-${cell?.row?.original?.pnl < 0 ? "danger" : "secondary"
                }`}
            >
              {cell?.row?.original?.pnl && Number(cell?.row?.original?.pnl)?.toFixed(2)}
            </span>
          );
        },
      },
      {
        header: "RoundID",
        accessorKey: "roundId",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return <span>{cell?.row?.original?.roundId}</span>;
        },
      },
      ...((showAction && betType === "open") && [{
        header: "",
        accessorKey: "action",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <>
            <Button className="btn-sm" onClick={() => handleStatusUpdate(cell?.row?.original?.id)} variant="info">
              Status
            </Button>
            {
              (actionType?.length !== 0 && cell?.row?.original?.market_Id === marketIdAction) && 
              <Button className="btn-sm ms-2" onClick={() => handleExpo?.(cell?.row?.original?.market_Id, cell?.row?.original?.id)} variant="info">
               {actionType ==="clearExposure"?"Clear Expo":"Result"}
              </Button>
            }


          </>

        ),
      }] || []),
    ],
    [actionType, betType]
  );

  return (
    <>
      <TableContainer
        columns={columns || []}
        data={defaultTable || []}
        isBordered={false}
        customPageSize={limit}
        isPagination={false}
        sorting={true}
        tableClass="table-centered align-middle table-nowrap table-striped mb-3"
        theadClass="text-muted table-light"
        SearchPlaceholder="Search Products..."
      />
      <nav aria-label="Page navigation" className="px-3 d-flex flex-column flex-sm-row align-items-center">
        <TableEntry
          limit={limit}
          handlelimitFilter={(value: number) => handlePageFilter(page, value)}
        />
        <ListPagination
          listTotal={listTotal}
          handlePageFilter={(value: number) => handlePageFilter(value, limit)}
        />
      </nav>

      <Modal
        show={showList}
        onHide={() => toggleList()}
        className="zoomIn"
        scrollable
      >
        <Modal.Header className="modal-title fw-bold d-flex justify-content-between pb-1">
          <div className="d-flex align-items-center flex-wrap">
            Parent List of{" "}
            <span className="text-muted fs-xs fw-normal mx-0 mx-sm-2 fs-xs">({selectedUser})</span>
          </div>
          <Button variant="light btn-sm" onClick={() => toggleList()}>
            <i className="ri-close-line fs-xl align-middle"></i>
          </Button>
        </Modal.Header>
        <Modal.Body>
          {agentList?.map((item) => {
            return (
              <p>{item.name} ({item.user_name})</p>
            );
          })}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CasinoBetsTable;
